// implements: CRS-V21-CTB-001 (useContextBadges + ContextBadgeSync)
import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/shared/api/client';
import { useSidebarStore } from '@/features/sidebar-store';
import type { SidebarContext, ContextTabBadge } from '@/shared/types';

// ─────────── API ───────────

async function fetchBadge(ctx: SidebarContext): Promise<ContextTabBadge | null> {
  try {
    const res = await apiClient.get<ContextTabBadge | null>(
      `/api/v1/sidebar/badge-summary?ctx=${ctx}`
    );
    return res.data ?? null;
  } catch {
    return null;
  }
}

async function fetchDBABadge(): Promise<ContextTabBadge | null> {
  try {
    const res = await apiClient.get<{ count: number }>('/api/v1/sidebar/badge-summary/dba');
    const count = res.data?.count ?? 0;
    if (count === 0) return null;
    return {
      count,
      type: 'numeric',
      color: count > 5 ? 'warning' : 'neutral',
      maxDisplay: 9,
    };
  } catch {
    return null;
  }
}

// ─────────── Badge query hook ───────────

const REFETCH_INTERVALS: Record<SidebarContext, number> = {
  team: 30_000,
  personal: 60_000,
  company: 60_000,
  role: 60_000,
};

function useBadgeQuery(ctx: SidebarContext) {
  return useQuery<ContextTabBadge | null>({
    queryKey: ['ctx-badge', ctx],
    queryFn: () => fetchBadge(ctx),
    refetchInterval: REFETCH_INTERVALS[ctx],
    staleTime: REFETCH_INTERVALS[ctx] * 0.9,
  });
}

function useDBARoleBadge(activeRoleId: string | null) {
  return useQuery<ContextTabBadge | null>({
    queryKey: ['ctx-badge', 'role', 'dba'],
    queryFn: fetchDBABadge,
    enabled: activeRoleId === 'dba',
    refetchInterval: 60_000,
    staleTime: 54_000,
  });
}

/**
 * ContextBadgeSync — mounts invisibly in AppLayout.
 * Polls badge summary for each context and syncs counts to the sidebar store.
 * War room pulse (pulsing red dot) is handled by SSE in SidebarRealtimeProvider.
 */
export function ContextBadgeSync() {
  const { setContextBadge, clearContextBadge, activeRoleId } = useSidebarStore();

  const personalBadge = useBadgeQuery('personal');
  const teamBadge = useBadgeQuery('team');
  const companyBadge = useBadgeQuery('company');
  const roleBadge = useBadgeQuery('role');
  const dbaBadge = useDBARoleBadge(activeRoleId);

  // Sync personal badge
  useEffect(() => {
    if (personalBadge.data != null) setContextBadge('personal', personalBadge.data);
    else if (!personalBadge.isLoading) clearContextBadge('personal');
  }, [personalBadge.data, personalBadge.isLoading, setContextBadge, clearContextBadge]);

  // Sync team badge
  useEffect(() => {
    if (teamBadge.data != null) setContextBadge('team', teamBadge.data);
    else if (!teamBadge.isLoading) clearContextBadge('team');
  }, [teamBadge.data, teamBadge.isLoading, setContextBadge, clearContextBadge]);

  // Sync company badge
  useEffect(() => {
    if (companyBadge.data != null) setContextBadge('company', companyBadge.data);
    else if (!companyBadge.isLoading) clearContextBadge('company');
  }, [companyBadge.data, companyBadge.isLoading, setContextBadge, clearContextBadge]);

  // Sync role badge (DBA role overrides generic role badge)
  useEffect(() => {
    const badge = activeRoleId === 'dba' ? dbaBadge.data : roleBadge.data;
    const loading = activeRoleId === 'dba' ? dbaBadge.isLoading : roleBadge.isLoading;
    if (badge != null) setContextBadge('role', badge);
    else if (!loading) clearContextBadge('role');
  }, [
    roleBadge.data, roleBadge.isLoading,
    dbaBadge.data, dbaBadge.isLoading,
    activeRoleId, setContextBadge, clearContextBadge,
  ]);

  return null; // renders nothing
}

/**
 * Badge display helper: clamp to "9+" when count > 9.
 */
export function formatBadgeDisplay(badge: ContextTabBadge | null): string | null {
  if (!badge || badge.count === 0) return null;
  const max = badge.maxDisplay ?? 9;
  return badge.count > max ? `${max}+` : String(badge.count);
}
